import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { useGetNpsReviews } from '../../../domain/Users';
import { Spinner } from '../../commonComponents/Spinner';
import { TabViewContainer } from '../../commonComponents/TabViewContainer';
import Styles from './NPSInfo.module.scss';

/**
 * Counts above this read as `999+`. The badge's own default of 99 would cap a
 * long-standing office's history at a number it has already passed.
 */
const MAX_BADGE_COUNT = 999;

/**
 * The NPS tab on the office and professional profiles.
 *
 * Reads auth-service rather than the monolith's `nps_reviews` array, which
 * stops growing once the apps post NPS to auth-service (GOTU-16477). Rows arrive
 * already labelled by the domain adapter, newest first as the API returns them.
 */
const NPSInfo = ({ userId }) => {
  const { rows, isPending, isError, refetch } = useGetNpsReviews(userId);
  const isSettled = !isPending && !isError;

  const content = () => {
    if (isPending) {
      return (
        <Box py={6}>
          <Spinner />
        </Box>
      );
    }

    if (isError) {
      return (
        <Alert
          severity="error"
          title="Unable to load NPS"
          description="The auth service may be unavailable. Please try again later."
          action={
            <Button variant="outlined" color="secondary" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        />
      );
    }

    if (rows.length === 0) {
      return (
        <Box py={4}>
          <Typography variant="body1" textAlign="center">
            No NPS
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <Typography variant="body2">{row.dateLabel}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.scoreLabel}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <TabViewContainer
      className={Styles.card}
      TitleTrailing={
        <Flex flex justify="end" align="center" pr={2}>
          {isSettled && (
            <Badge
              variant="counter"
              severity="secondary"
              count={rows.length}
              max={MAX_BADGE_COUNT}
              aria-label={`${rows.length} NPS answers`}
            />
          )}
        </Flex>
      }
      title="NPS"
      variant="card"
    >
      {content()}
    </TabViewContainer>
  );
};

NPSInfo.propTypes = {
  /** The profile's user id. Absent while the profile resolves. */
  userId: PropTypes.string,
};

NPSInfo.defaultProps = {
  userId: undefined,
};

export default NPSInfo;
